/**
 * @agion/ui/api-failure — the failure contract between an app's HTTP adapter
 * and its pages (ADR-20260925). One implementation for admin-ui and chat-ui:
 *
 * - The adapter throws an ApiFailure (or a subclass): what happened, as typed
 *   fields. Its message is a diagnostic for logs and never reaches rendered text.
 * - failureReason() is the one mapper from a failure to the words a person
 *   reads. The page says what it was doing (its own title); the mapper says
 *   why it did not happen.
 * - Code that reacts to a failure narrows with isApiFailure() and reads
 *   status, code and data, never a transport library's response shape.
 *
 * Pure TypeScript with no React and no i18n runtime: each app passes its own
 * translate function, and carries the `common:nextFailure.*` keys.
 */

export type ApiFailureKind = 'http' | 'unauthorized' | 'network' | 'cancelled';

export interface ApiFailureInit {
  kind: ApiFailureKind;
  /** The HTTP status; omitted (0) when no response arrived. */
  status?: number;
  /** The server's body, as sent. */
  data?: unknown;
  /** The server's machine code when the adapter read it itself; derived from `data` when omitted. */
  code?: string;
}

export class ApiFailure extends Error {
  readonly kind: ApiFailureKind;
  /** The HTTP status; 0 when no response arrived. */
  readonly status: number;
  /** The server's machine code (`code`, or `error` when it is a single token). */
  readonly code: string | undefined;
  /** The server's body, as sent. */
  readonly data: unknown;
  /** Worth offering a retry: no response, a timeout, throttling or a server fault. */
  readonly retryable: boolean;

  constructor(message: string, init: ApiFailureInit) {
    super(message);
    this.name = 'ApiFailure';
    this.kind = init.kind;
    this.status = init.status ?? 0;
    this.data = init.data;
    this.code = init.code ?? failureCode(init.data);
    this.retryable =
      init.kind === 'network' || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

export function isApiFailure(error: unknown): error is ApiFailure {
  return error instanceof ApiFailure;
}

const CODE_TOKEN = /^[a-z0-9_.-]+$/i;

/** The server's machine code in a body: `code`, else `error` when it is a single token such as "mission_not_found". */
export function failureCode(data: unknown): string | undefined {
  if (data === null || typeof data !== 'object') return undefined;
  const fields = data as Record<string, unknown>;
  if (typeof fields['code'] === 'string' && fields['code'] !== '') return fields['code'];
  const error = fields['error'];
  if (typeof error === 'string' && CODE_TOKEN.test(error)) return error;
  return undefined;
}

/**
 * The sentence of a refusal body: `message`, then `error`, then `detail`, each
 * only as a non-empty string. `error` comes after `message` because a service
 * may put a code or the status text ("Bad Request") there beside the sentence.
 */
export function refusalText(body: unknown): string | undefined {
  const fields = body !== null && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  for (const field of ['message', 'error', 'detail']) {
    const text = fields[field];
    if (typeof text === 'string' && text !== '') return text;
  }
  return undefined;
}

/** A server sentence a person could read: plain text or a refusal body's sentence, never a bare code or an HTML page. */
function sentenceOf(data: unknown): string | undefined {
  const text = typeof data === 'string' ? data.trim() : refusalText(data);
  if (!text || text.startsWith('<') || CODE_TOKEN.test(text)) return undefined;
  return text;
}

export type FailureClass =
  | 'network'
  | 'unavailable'
  | 'session'
  | 'forbidden'
  | 'missing'
  | 'refused'
  | 'unknown';

export function failureClass(error: unknown): FailureClass {
  if (!isApiFailure(error)) {
    // A transport library's own timeout (ky's TimeoutError) never reaches the
    // adapter's hooks; it means the service did not answer in time.
    return error instanceof Error && error.name === 'TimeoutError' ? 'unavailable' : 'unknown';
  }
  if (error.kind === 'network') return 'network';
  if (error.kind === 'unauthorized') return 'session';
  // A 401 that words a reason refuses what the person asked (a wrong current
  // password); a bare 401 is a lost session.
  if (error.status === 401) return sentenceOf(error.data) ? 'refused' : 'session';
  if (error.status === 403) return 'forbidden';
  if (error.status === 404) return 'missing';
  if (error.retryable) return 'unavailable';
  if (error.status >= 400 && error.status < 500) return 'refused';
  return 'unknown';
}

/** Whether a retry can help: never for a refusal, a missing item or a lost session. */
export function canRetry(error: unknown): boolean {
  const kind = failureClass(error);
  return kind === 'network' || kind === 'unavailable' || kind === 'unknown';
}

/** An app's translate function: key, English default, interpolation values. */
export type FailureTranslate = (key: string, defaultValue: string, options?: Record<string, unknown>) => string;

export interface FailureReasonOptions {
  /**
   * When the server's own sentence may reach the reader. `refusal`: for a
   * refusal of what the person asked (a 4xx other than 403 and 404, and a 401
   * that words a reason), which services word for people (validation,
   * conflicts, password rules). `never` (the default): a read's failure, where
   * the server's text describes internals (a SQL validator, a missing row).
   * Server text never passes through for a 5xx.
   */
  serverText?: 'refusal' | 'never';
}

export function failureReason(error: unknown, t: FailureTranslate, options: FailureReasonOptions = {}): string {
  const kind = failureClass(error);
  if (kind === 'refused' && options.serverText === 'refusal' && isApiFailure(error)) {
    const sentence = sentenceOf(error.data);
    if (sentence) return sentence;
  }
  switch (kind) {
    case 'network':
      return t('common:nextFailure.network', 'The connection to Agion failed. Check your network and try again.');
    case 'unavailable':
      return t('common:nextFailure.unavailable', 'The service is not responding right now. Try again in a moment.');
    case 'session':
      return t('common:nextFailure.session', 'Your session has ended. Sign in again to continue.');
    case 'forbidden':
      return t('common:nextFailure.forbidden', 'Your role does not allow this.');
    case 'missing':
      return t('common:nextFailure.missing', 'It no longer exists or has moved.');
    case 'refused':
      return t('common:nextFailure.refused', 'The request was refused.');
    default:
      return t('common:nextFailure.unknown', 'Something went wrong. Try again.');
  }
}
